import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { IconButton, LoadingSpinner } from "cprime-foundational-components";
import Button from "../Common/CprimeButton";
import CloseIcon from "@mui/icons-material/Close";
import * as styles from "./RoleActivateDeactivateDialog.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import {
  roleStatusUpdate,
  setActivateButtonDisable,
  verifyRole,
} from "../../store/slices/CreateRoleSlice";
import { triggerToast } from "../../utils/toast";
import { incrementTableRefresh } from "../../store/slices/apiDetailsSlice";

interface RoleActivateDeactivateDialogProps {
  open: boolean;
  onClose: () => void;
  data: any;
}

type Feature = {
  feature_name: string;
  permissions: string;
};
type Api = {
  api_name: string;
  methods: string;
};
type Domain = {
  domain_name: string;
  allowed_subdomains: string[];
  denied_subdomains: string[];
};

const RoleActivateDeactivateDialog: React.FC<
  RoleActivateDeactivateDialogProps
> = ({ open, onClose, data }) => {
  const scssClass = styles as any;
  const dispatch = useDispatch<AppDispatch>();

  const [features, setFeatures] = useState<Feature[]>([]);
  const [apis, setApis] = useState<Api[]>([]);
  const [domain, setDomain] = useState<Domain[]>([]);
  const [description, setDescription] = useState("");
  const { data: appData } = useSelector(
    (state: RootState) => state.applicationDetails,
  );
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleModalConfirm = async () => {
    setLoading(true);
    try {
      if (!data || !data.role_id || !data.status) {
        throw new Error("Missing required role data.");
      }
      const result = await dispatch(
        roleStatusUpdate({ roleId: data.role_id, status: data?.status }),
      ).unwrap();
      if (result.status !== "SUCCESS") {
        throw new Error("User does not have permission to modify this role");
      }
      triggerToast("Status Updated Succesfully", "success");
      dispatch(setActivateButtonDisable(true));
      onClose();
      dispatch(incrementTableRefresh());
    } catch (error: any) {
      // console.error("Error in handleModalConfirm:", error);
    } finally {
      setLoading(false);
    }
  };

  const verifyRoleDetails = async (role_id: number) => {
    setDetailsLoading(true);
    try {
      // if (typeof data.role_id !== 'string' || data.role_id.trim() === '') {
      // if (
      //   !data ||
      //   !data.role_id ||
      //   typeof data.role_id !== "string" ||
      //   data.role_id.trim() === ""
      // ) {
      //   throw new Error("Missing or invalid role_id to verify.");
      // }
      const result = await dispatch(verifyRole(role_id)).unwrap();
      setDescription(result[0].description || "");
      setFeatures(result[0].features || []);
      setApis(result[0].apis || []);
      setDomain(result[0].domain || []);
    } catch (error) {
      // console.error("Error verifying role:", error);
    } finally {
      setDetailsLoading(false);
    }
  };

  useEffect(() => {
    const roleId = data?.role_id;
    if (roleId == null || roleId === "") return;
    const numericRoleId = typeof roleId === "number" ? roleId : Number(roleId);
    if (!Number.isNaN(numericRoleId)) {
      verifyRoleDetails(numericRoleId);
    }
  }, [data?.role_id]);

  return (
    <Dialog
      open={open}
      //  onClose={onClose}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle className={scssClass.roleDialogHeader}>
        Verify Your Role
        <IconButton
          aria-label="close"
          onClick={onClose}
          name="Close"
          sx={{ position: "absolute", right: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent className={scssClass.roleDialogContentDiv}>
        {detailsLoading ? (
          <LoadingSpinner />
        ) : (
          <>
            {data ? (
              <div>
                <p className={scssClass.roleDialogSubHeading}>
                  Review your role one last time
                </p>
                <div className={scssClass.roleDialogBody}>
                  <div className={scssClass.roleDialogRow}>
                    <div className={scssClass.roleDialogRowLable}>
                      Application name
                    </div>
                    <div className={scssClass.roleDialogRowData}>
                      {appData?.app_cmdb_name}
                    </div>
                  </div>
                  <div className={scssClass.roleDialogRow}>
                    <div className={scssClass.roleDialogRowLable}>
                      Description
                    </div>
                    <div className={scssClass.roleDialogRowData}>
                      {description}
                    </div>
                  </div>
                  <div className={scssClass.roleDialogRow}>
                    <div className={scssClass.roleDialogRowLable}>
                      Features and their permissions
                    </div>
                    <div className={scssClass.roleDialogRowData}>
                      {features
                        .map(
                          (item) =>
                            `${item?.permissions}:${item?.feature_name}`,
                        )
                        ?.join(", ")}
                    </div>
                  </div>
                  <div className={scssClass.roleDialogRow}>
                    <div className={scssClass.roleDialogRowLable}>
                      API Details
                    </div>
                    <div className={scssClass.roleDialogRowData}>
                      {apis
                        .map((item) => `${item?.api_name}:${item?.methods}`)
                        ?.join(", ")}
                    </div>
                  </div>
                  <div className={scssClass.roleDialogRow}>
                    <div className={scssClass.roleDialogRowLable}>
                      Data Restriction
                    </div>
                    <div className={scssClass.roleDialogRowData}>
                      {domain.map((d, index) => (
                        <div key={index}>
                          <strong>{d?.domain_name}</strong>
                          <br />
                          Allowed: {d?.allowed_subdomains?.join(", ")}
                          <br />
                          Denied: {d?.denied_subdomains?.join(", ")}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div>No data available.</div>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions className={scssClass.roleDialogAction}>
        <Button
          color="primary"
          onClick={() => handleModalConfirm()}
          loading={loading}
          disabled={detailsLoading}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RoleActivateDeactivateDialog;
